import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Check, Send } from "lucide-react";
import { FayeAppBar } from "components/layout/NavigationLayout";
import {
  PaymentElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import useIsMobile from "components/hooks/useIsMobile";
import useGoogleTracking, {
  GOOGLE_CONVERSION_LABEL_PAYMENT,
  GOOGLE_TAG_ID,
  googleEventNamePayment,
} from "components/hooks/useGoogleTracking";
import { useCreatePaymentIntent } from "services/billing";
import {
  useUpgradeDigitalSubscription,
  useNotifySupportUpgradeRequest,
} from "services/member";
import { useGetSignupSessionForMember } from "services/signup";
import { BillingProduct } from "protogen/billing_pb";
import PromoCodeForm, {
  PromoCodeState,
} from "components/signup/components/PromoCodeForm";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const DIGITAL_PLAN_KEYS = new Set(["access", "basic"]);
const DIGITAL_PLAN_BADGE: Record<string, string> = {};
const AI_FAMILY_ASSISTANT_FEATURES = [
  "2 member accounts",
  "Includes AI tasks, reminders, & calendaring",
  "Human support upon request @ $1/min",
];

function normalizePlanKey(name: string): string | null {
  const lower = name.trim().toLowerCase();
  if (!lower.startsWith("digital ")) return null;
  const key = lower.slice(8).trim();
  return DIGITAL_PLAN_KEYS.has(key) ? key : null;
}

function displayProductName(product: BillingProduct): string {
  return normalizePlanKey(product.name) === "basic" ? "Faye AI" : product.name;
}

const appearance = {
  theme: "stripe" as const,
  variables: {
    colorPrimary: "#1976d2",
    colorBackground: "#ffffff",
    colorText: "#3D3D3D",
    colorDanger: "#C14743",
    fontFamily: "AlbertSans, SourceSerifPro, sans-serif",
    borderRadius: "16px",
  },
};

const PlanCardShell = ({
  isSelected,
  onClick,
  children,
}: {
  isSelected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      border: isSelected
        ? `2px solid ${theme.palette.teal[4]}`
        : `1px solid ${theme.palette.border}`,
      borderRadius: "16px",
      padding: isSelected ? "15px 19px" : "16px 20px",
      display: "flex",
      flexDirection: "column",
      backgroundColor: isSelected ? theme.palette.teal[1] : "white",
      gap: "2px",
      cursor: "pointer",
    })}
  >
    {children}
  </Box>
);

const DigitalPlanCard = ({
  product,
  planKey,
  isSelected,
  onClick,
}: {
  product: BillingProduct;
  planKey: string;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const badge = DIGITAL_PLAN_BADGE[planKey];

  return (
    <PlanCardShell isSelected={isSelected} onClick={onClick}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h4">{displayProductName(product)}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
            justifyContent: "start",
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Typography variant="h4">
              ${(product.amountCents / 100).toLocaleString("en-US")}
            </Typography>
            <Typography variant="body">/mo</Typography>
          </Box>
          {badge && (
            <Chip
              label={badge}
              variant={"green" as any}
              size="small"
              sx={{
                border: "1px solid transparent",
                "& .MuiChip-label": { fontSize: "13px" },
              }}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "12px", mt: 2 }}
      >
        {AI_FAMILY_ASSISTANT_FEATURES.map((feature) => (
          <Box
            key={feature}
            sx={{
              display: "grid",
              gridTemplateColumns: "20px 1fr",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Check size={20} color="#198282" strokeWidth={2.4} />
            <Typography variant="body">{feature}</Typography>
          </Box>
        ))}
      </Box>
    </PlanCardShell>
  );
};

const DedicatedAssistantCard = ({
  isSelected,
  onClick,
}: {
  isSelected: boolean;
  onClick: () => void;
}) => (
  <PlanCardShell isSelected={isSelected} onClick={onClick}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h4">Dedicated Advisor</Typography>
        <Typography variant="bodyHeavy" sx={{ color: "#198282" }}>
          Work 1:1 with a dedicated Faye Advisor
        </Typography>
      </Box>
      <Typography variant="h4">$169+</Typography>
    </Box>
  </PlanCardShell>
);

const StripePaymentWrapper = ({
  setStripeInstance,
  setElementsInstance,
  onReady,
}: {
  setStripeInstance: (s: Stripe) => void;
  setElementsInstance: (e: StripeElements) => void;
  onReady: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (stripe) setStripeInstance(stripe);
  }, [stripe, setStripeInstance]);

  useEffect(() => {
    if (elements) setElementsInstance(elements);
  }, [elements, setElementsInstance]);

  return <PaymentElement onReady={onReady} options={{ layout: "tabs" }} />;
};

const DEDICATED_IDX = -1;

const CheckoutForm = ({
  products,
  initialProductId,
  onBack,
}: {
  products: BillingProduct[];
  initialProductId?: string;
  onBack: () => void;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedIdx, setSelectedIdx] = useState(() => {
    if (initialProductId) {
      const idx = products.findIndex((p) => p.id === initialProductId);
      if (idx !== -1) return idx;
    }
    return 0;
  });
  const [clientSecret, setClientSecret] = useState("");
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [elementsInstance, setElementsInstance] =
    useState<StripeElements | null>(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dedicatedRequested, setDedicatedRequested] = useState(false);
  const [dedicatedError, setDedicatedError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<PromoCodeState | undefined>(
    undefined,
  );
  const [initialPromoCode, setInitialPromoCode] = useState<string | undefined>(
    undefined,
  );
  const { request: getSignupSession } = useGetSignupSessionForMember();
  useEffect(() => {
    getSignupSession({}).then((r) => {
      if (r?.state?.referralCode) {
        setInitialPromoCode(r.state.referralCode);
      }
    });
  }, []);
  const { fireConversionEvent } = useGoogleTracking(GOOGLE_TAG_ID);
  const { request: createIntent } = useCreatePaymentIntent((r) =>
    setClientSecret(r.clientSecret),
  );
  const { request: upgradeSubscription } = useUpgradeDigitalSubscription();
  const { request: notifyUpgrade, loading: notifyUpgradeLoading } =
    useNotifySupportUpgradeRequest();

  const isDedicated = selectedIdx === DEDICATED_IDX;
  const selectedProduct = isDedicated ? null : products[selectedIdx];
  const selectedProductDisplayName = selectedProduct
    ? displayProductName(selectedProduct)
    : "";

  useEffect(() => {
    if (selectedProduct) {
      setClientSecret("");
      setPaymentReady(false);
      createIntent({
        productName: selectedProduct.name,
        priceAmountCents: selectedProduct.amountCents,
      });
    }
  }, [selectedIdx]);

  const handleSubmit = useCallback(async () => {
    if (!stripeInstance || !elementsInstance || !selectedProduct) return;
    setLoading(true);
    setError(null);

    const { error: submitError } = await elementsInstance.submit();
    if (submitError) {
      setError(submitError.message || "An error occurred submitting payment.");
      setLoading(false);
      return;
    }

    const { paymentMethod, error: pmError } =
      await stripeInstance.createPaymentMethod({ elements: elementsInstance });
    if (pmError || !paymentMethod) {
      setError(pmError?.message || "Failed to create payment method.");
      setLoading(false);
      return;
    }

    try {
      await upgradeSubscription({
        productName: selectedProduct.name,
        priceAmountCents: selectedProduct.amountCents,
        paymentMethodId: paymentMethod.id,
        couponId: promoCode?.coupon?.id || "",
      });
      fireConversionEvent(
        GOOGLE_CONVERSION_LABEL_PAYMENT,
        googleEventNamePayment(),
      );
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upgrade plan.");
    } finally {
      setLoading(false);
    }
  }, [
    stripeInstance,
    elementsInstance,
    selectedProduct,
    promoCode,
    upgradeSubscription,
    fireConversionEvent,
    navigate,
  ]);

  const handleDedicatedInquiry = useCallback(async () => {
    if (dedicatedRequested) return;

    setDedicatedError(null);
    const response = await notifyUpgrade({});
    if (response) {
      setDedicatedRequested(true);
      return;
    }

    setDedicatedError("We couldn't send your request. Please try again.");
  }, [dedicatedRequested, notifyUpgrade]);

  const planCards = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {products.map((product, idx) => {
        const planKey = normalizePlanKey(product.name) ?? "";
        return (
          <DigitalPlanCard
            key={product.id}
            product={product}
            planKey={planKey}
            isSelected={selectedIdx === idx}
            onClick={() => setSelectedIdx(idx)}
          />
        );
      })}
      <DedicatedAssistantCard
        isSelected={isDedicated}
        onClick={() => setSelectedIdx(DEDICATED_IDX)}
      />
    </Box>
  );

  const dedicatedPanel = (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: `1px solid ${theme.palette.border}`,
        padding: isMobile ? "20px" : "24px 27px",
        gap: "8px",
        backgroundColor: "#FFF",
      })}
    >
      <Typography variant="h3">Dedicated Advisor</Typography>
      <Typography variant="body" color="textSecondary">
        Get a dedicated advisor who knows your family and handles tasks
        proactively.
      </Typography>
      <Typography
        variant="body"
        color="textSecondary"
        sx={{ marginTop: "8px" }}
      >
        Send an inquiry and someone from the Faye team will reach out to get you
        set up.
      </Typography>
      {dedicatedRequested && (
        <Alert severity="success" icon={false} sx={{ mt: 1 }}>
          Thanks, we got your request. Someone from the Faye team will reach out
          shortly.
        </Alert>
      )}
      {dedicatedError && (
        <Alert severity="error" icon={false} sx={{ mt: 1 }}>
          {dedicatedError}
        </Alert>
      )}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={notifyUpgradeLoading || dedicatedRequested}
        onClick={handleDedicatedInquiry}
        startIcon={!notifyUpgradeLoading ? <Send size={18} /> : undefined}
        sx={{ mt: 1 }}
      >
        {notifyUpgradeLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : dedicatedRequested ? (
          "Inquiry sent"
        ) : (
          "Inquire"
        )}
      </Button>
    </Box>
  );

  const paymentPanel = (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: `1px solid ${theme.palette.border}`,
        padding: isMobile ? "20px" : "24px 27px",
        gap: "16px",
        backgroundColor: "#FFF",
      })}
    >
      <Box>
        <Typography variant="h3" sx={{ marginBottom: "4px" }}>
          {selectedProductDisplayName}
        </Typography>
        <Typography variant="body" color="textSecondary">
          ${((selectedProduct?.amountCents ?? 0) / 100).toFixed(2)}/mo · billed
          monthly
        </Typography>
      </Box>

      <PromoCodeForm
        key={initialPromoCode ?? ""}
        initialCode={initialPromoCode}
        onChange={(state) =>
          setPromoCode(state.validated === "validated" ? state : undefined)
        }
      />

      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={
            { clientSecret, appearance, paymentMethodCreation: "manual" } as any
          }
        >
          <StripePaymentWrapper
            setStripeInstance={setStripeInstance}
            setElementsInstance={setElementsInstance}
            onReady={() => setPaymentReady(true)}
          />
        </Elements>
      )}

      {error && (
        <Alert severity="error" icon={false}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading || !paymentReady}
        onClick={handleSubmit}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Subscribe to ${selectedProductDisplayName}`
        )}
      </Button>
    </Box>
  );

  const activePanel = isDedicated ? dedicatedPanel : paymentPanel;

  const header = (
    <Box sx={{ marginBottom: "24px" }}>
      <Button variant="text" onClick={onBack} sx={{ mb: 1 }}>
        ← Back
      </Button>
      <Typography variant="h2">Choose your plan</Typography>
    </Box>
  );

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {header}
        {planCards}
        {activePanel}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "32px",
        alignItems: "start",
      }}
    >
      <Box>
        {header}
        {planCards}
      </Box>
      <Box sx={{ position: "sticky", top: "24px" }}>{activePanel}</Box>
    </Box>
  );
};

export default ({
  products,
  initialProductId,
}: {
  products: BillingProduct[];
  initialProductId?: string;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  if (products.length === 0) {
    return null;
  }

  return (
    <>
      <FayeAppBar />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          paddingTop: "50px",
        }}
      >
        <Box
          sx={{
            maxWidth: "900px",
            width: "100%",
            padding: isMobile ? "16px" : "32px",
          }}
        >
          <CheckoutForm
            products={products}
            initialProductId={initialProductId}
            onBack={() => navigate(-1)}
          />
        </Box>
      </Box>
    </>
  );
};
